// Modification : Zacharie Nolet
// Date de modification : 2026-05-12
// Commit : 4f70ae9 - Fix bug home
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../../utils/Api';
import './Home.css';

import { Switch, FormControlLabel, Avatar } from '@mui/material';
import { AuthContext } from '../../context/AuthContext';

import BadgeComponent from '../../composant/PageProfil/BadgeComponent';
import Role from '../../policies/Role';

import TeacherHomePanel from './components/TeacherHomePanel';
import StudentHomeFallback from './components/StudentHomeFallback';

function useGlobalFeed() {
  const [globalFeedEnabled, setGlobalFeedEnabled] = useState(true);

  useEffect(() => {
    axios
      .get('/settings/feed')
      .then((res) => setGlobalFeedEnabled(!!res.data.enabled))
      .catch(() => setGlobalFeedEnabled(true));
  }, []);

  return { globalFeedEnabled };
}

export default function Home() {
  const location = useLocation();

  const { user, loading: loadingAuth } = useContext(AuthContext);
  const { globalFeedEnabled } = useGlobalFeed();

  const [allNotifications, setAllNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [localFeedEnabled, setLocalFeedEnabled] = useState(true);

  const isAdmin =
    user?.role === Role.Admin || user?.role === Role.AdminContact;

  const isTeacher = user?.role === Role.Teacher;
  const isStudent = !isAdmin && !isTeacher;

  const isFeedPage = location.pathname === '/feed';

  const canToggleFeed = isTeacher || isAdmin;

  const feedEnabled = isAdmin
    ? true
    : isTeacher
      ? localFeedEnabled || isFeedPage
      : globalFeedEnabled;

  useEffect(() => {
    if (loadingAuth) return;

    if (isTeacher) {
      const userId = user?.id || user?.username || 'default';
      const savedValue = localStorage.getItem(`feedEnabled_${userId}`);

      if (savedValue === null) {
        setLocalFeedEnabled(false);
      } else {
        setLocalFeedEnabled(savedValue === 'true');
      }
    }
  }, [loadingAuth, user, isTeacher]);

  useEffect(() => {
    if (loadingAuth) return;

    if (!feedEnabled) {
      setAllNotifications([]);
      setLoadingNotifications(false);
      return;
    }

    setLoadingNotifications(true);

    axios
      .get('/notification')
      .then((res) => {
        const filteredNotifications = (res.data || []).filter(
          (notif) => notif.type !== 'system_reset'
        );

        setAllNotifications(filteredNotifications);
      })
      .catch(() => setAllNotifications([]))
      .finally(() => setLoadingNotifications(false));
  }, [loadingAuth, feedEnabled]);

  const handleDelete = (id) => {
    setAllNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleToggleFeed = (event) => {
    const newValue = event.target.checked;

    setLocalFeedEnabled(newValue);

    const userId = user?.id || user?.username || 'default';
    localStorage.setItem(`feedEnabled_${userId}`, String(newValue));
  };

  if (loadingAuth) {
    return <div className="home-loading">Chargement...</div>;
  }

  return (
    <div className="home home-no-sidebar">
      <main className="home-main">
        <div className="home-content">
          <div className="home-header">
            <div>
              <h2 className="home-title">
                {feedEnabled ? "Fil d'actualité" : 'Tableau de bord'}
              </h2>

              <p className="home-subtitle">
                Suivez les activités et performances en temps réel
              </p>
            </div>

            {canToggleFeed && !isFeedPage && (
              <FormControlLabel
                control={
                  <Switch
                    checked={localFeedEnabled}
                    onChange={handleToggleFeed}
                  />
                }
                label={localFeedEnabled ? 'Fil activé' : 'Fil désactivé'}
              />
            )}
          </div>

          {isTeacher && !feedEnabled ? (
            <TeacherHomePanel user={user} />
          ) : !feedEnabled && isStudent ? (
            <StudentHomeFallback user={user} />
          ) : loadingNotifications ? (
            <div className="home-loading">Chargement...</div>
          ) : (
            <>
              {allNotifications.length === 0 ? (
                <div className="home-empty">
                  Aucune actualité pour le moment.
                </div>
              ) : (
                <div className="feed">
                  {allNotifications.map((notif) => (
                    <NotificationCard
                      key={notif.id}
                      notif={notif}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function NotificationCard({ notif, onDelete }) {
  const getMessage = () => {
    const name = notif.user
      ? `${notif.user.first_name} ${notif.user.last_name}`
      : 'Inconnu';

    switch (notif.type) {
      case 'badge_created':
        return `${name} a créé un badge`;

      case 'badge_earned':
        return `${name} a gagné un badge`;

      case 'ranking':
        return `${name} est maintenant ${notif.rank}${
          notif.rank === 1 ? 'ère' : 'ème'
        } position`;

      default:
        return 'Notification';
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);

    if (Number.isNaN(d.getTime())) {
      return '';
    }

    return d.toLocaleDateString('fr-CA');
  };

  const renderUserAvatar = (size = 64) => {
    return (
      <Avatar
        src={notif.user?.avatarImagePath}
        alt={notif.user?.first_name || 'user'}
        sx={{
          width: size,
          height: size,
          border: '2px solid #1976d2',
          flexShrink: 0,
        }}
      >
        {notif.user?.first_name?.charAt(0) || '?'}
      </Avatar>
    );
  };

  const renderContent = () => {
    switch (notif.type) {
      case 'badge_created':
        return (
          <div className="card-body card-center">
            <div className="feed-badge-box">
              {notif.badge && <BadgeComponent badge={notif.badge} />}
            </div>
          </div>
        );

      case 'badge_earned':
        return (
          <div className="card-body card-earned">
            <div className="feed-avatar-box">{renderUserAvatar(64)}</div>
            <div className="feed-separator">•</div>
            <div className="feed-badge-box">
              {notif.badge && <BadgeComponent badge={notif.badge} />}
            </div>
          </div>
        );

      case 'ranking':
        return (
          <div className="card-body card-center">
            <div className="card-avatar-wrapper card-avatar-large">
              {renderUserAvatar(96)}
              <div className={`card-rank card-rank-${notif.rank}`}>
                #{notif.rank}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-info">
          <div>
            <p className="card-title">{getMessage()}</p>
            <p className="card-date">{formatDate(notif.created_at)}</p>
          </div>
        </div>
      </div>

      {renderContent()}
    </div>
  );
}